import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { DeceasedView } from '@wodi/shared';
import { Notice, Section } from '@wodi/ui';

import { patientBirthsQuery } from '../api/vital-events.ts';
import { formatInstant } from '../patients/format.ts';
import styles from './vital-events.module.css';
import { errorText, gramsText } from './words.ts';

// On the patient record (docs/plans/phase2-mortuary-and-notifications.md §6): that the person
// died, with a link to the death record, and the births linked to them.

export function DeceasedNotice({
  deceased,
  canReadDeaths,
}: {
  deceased: DeceasedView | null;
  canReadDeaths: boolean;
}) {
  if (deceased === null) return null;
  return (
    <Notice
      tone="info"
      {...(canReadDeaths
        ? {
            action: (
              <Link to="/deaths/$deathId" params={{ deathId: deceased.deathId }}>
                Open the death record
              </Link>
            ),
          }
        : {})}
    >
      {`Died on ${formatInstant(deceased.diedAt)}. New visits, appointments and SMS reminders are stopped.`}
    </Notice>
  );
}

export function PatientBirthsSection({ patientId }: { patientId: string }) {
  const births = useQuery(patientBirthsQuery(patientId));
  if (births.isPending) return null;
  if (births.isError)
    return <Notice tone="error">{errorText(births.error, 'Births could not be loaded.')}</Notice>;
  const { asMother, asBaby } = births.data;
  if (asMother.length === 0 && asBaby === null) return null;
  return (
    <Section title="Births">
      <ul className={styles.list} aria-label="Births">
        {asBaby === null ? null : (
          <li className={styles.listRow}>
            <span>
              {`Born here on ${formatInstant(asBaby.bornAt)}, mother `}
              <Link to="/patients/$patientId" params={{ patientId: asBaby.mother.id }}>
                {asBaby.mother.displayName}
              </Link>
            </span>
            <Link to="/births/$birthId" params={{ birthId: asBaby.id }}>
              Birth notification
            </Link>
          </li>
        )}
        {asMother.map((b) => (
          <li key={b.id} className={styles.listRow}>
            <span>
              <Link to="/patients/$patientId" params={{ patientId: b.baby.id }}>
                {b.baby.displayName}
              </Link>
              {`, born ${formatInstant(b.bornAt)}, ${gramsText(b.birthWeightGrams)}${b.status === 'entered_in_error' ? ', entered in error' : ''}`}
            </span>
            <Link to="/births/$birthId" params={{ birthId: b.id }}>
              Birth notification
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}
